/**
 * Facebook Pixel — injected dynamically so we can no-op when the env var
 * is absent (e.g. local dev). For everything beyond PageView, fire via
 * `fireCapi()` in lib/capi.ts — that pairs the browser Pixel call with
 * a server-side CAPI POST under the same `eventID`, so Meta dedupes
 * instead of double-counting. Don't call `fbqTrack` directly from app
 * code unless you have a deliberate reason to skip the server path.
 */

import { isPaidTraffic } from "./paidTraffic";

const PIXEL_ID = import.meta.env.VITE_FB_PIXEL_ID;

let initialized = false;

export function initFbPixel() {
  if (initialized || !PIXEL_ID || typeof window === "undefined") return;
  // Paid-only injection. Organic / direct / internal traffic is excluded
  // from Meta entirely (every CAPI + Pixel event in lib/capi.ts already
  // early-returns for it), so there's no reason to pull the ~98 KB
  // `fbevents.js` for those visitors — it's pure weight on the landing
  // screen, especially on mobile data. Gating the whole injection here
  // means internal QA and organic landers never download the pixel at
  // all; paid visitors still get it (async) for the paired Pixel+CAPI
  // dedup. `isPaidTraffic()` reads the cached landing decision, so it's
  // stable for the rest of the session.
  if (!isPaidTraffic()) return;
  initialized = true;

  /* eslint-disable */
  // Standard FB Pixel base snippet, lightly modernized.
  ((f: any, b: Document, e: string, v: string) => {
    if (f.fbq) return;
    const n: any = (f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0]!;
    s.parentNode!.insertBefore(t, s);
  })(
    window as any,
    document,
    "script",
    "https://connect.facebook.net/en_US/fbevents.js"
  );
  /* eslint-enable */

  window.fbq?.("init", PIXEL_ID);
  // Paid-only: skip the base PageView for organic / direct / internal
  // traffic so internal testing never reaches Meta. Conversion events
  // are gated separately in lib/capi.ts. (The init above still runs so
  // `fbq` exists for the paired Pixel calls fireCapi makes on paid hits.)
  if (isPaidTraffic()) window.fbq?.("track", "PageView");
}

export function fbqTrack(
  event: string,
  params?: Record<string, unknown>,
  options?: { eventID?: string }
) {
  if (!window.fbq) return;
  // Standard events go through `track`; anything else is a CUSTOM event
  // and must use `trackCustom`, or the Pixel logs a warning and Events
  // Manager flags it as an unrecognized standard event. Auto-routing here
  // means call sites just pass the event name and get the right method.
  const method = STANDARD_PIXEL_EVENTS.has(event) ? "track" : "trackCustom";
  if (options?.eventID) {
    window.fbq(method, event, params ?? {}, { eventID: options.eventID });
  } else {
    window.fbq(method, event, params ?? {});
  }
}

/**
 * Meta's canonical standard-event vocabulary. Names NOT in this set are
 * treated as custom events (fired via `trackCustom`). Keep in sync with
 * Meta's standard-events list.
 */
const STANDARD_PIXEL_EVENTS = new Set<string>([
  "PageView",
  "ViewContent",
  "Search",
  "AddToCart",
  "AddToWishlist",
  "InitiateCheckout",
  "AddPaymentInfo",
  "Purchase",
  "Lead",
  "CompleteRegistration",
  "Contact",
  "CustomizeProduct",
  "Donate",
  "FindLocation",
  "Schedule",
  "StartTrial",
  "SubmitApplication",
  "Subscribe",
]);

// Pixel-only convenience helpers (fbqLead, fbqInitiateCheckout,
// fbqPurchase) used to live here — they were removed in favor of
// fireCapi() so every funnel event is sent through both Pixel + CAPI
// with a shared eventID. If you genuinely need a Pixel-only fire,
// call fbqTrack(name, params) directly.
